import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { getStatusBadge, getWeek } from './timesheetHelpers';

/**
 * Export filtered entries to Excel
 */
export const exportToExcel = (filteredEntries) => {
    try {
        const exportData = filteredEntries.map(entry => {
            const statusBadge = getStatusBadge(entry.status);
            return {
                'Date': new Date(entry.date).toLocaleDateString('fr-FR'),
                'Employé': entry.expand?.worker?.name || entry.expand?.worker?.username || 'Inconnu',
                'Chantier': entry.expand?.chantier?.name || '-',
                'Heures': entry.hours,
                'Description': entry.description || '-',
                'Statut': statusBadge.label,
                'Commentaire': entry.validation_comment || '-'
            };
        });

        const ws = XLSX.utils.json_to_sheet(exportData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Heures');

        ws['!cols'] = [
            { wch: 12 }, { wch: 20 }, { wch: 25 },
            { wch: 8 }, { wch: 40 }, { wch: 12 }, { wch: 30 }
        ];

        const filename = `Heures_${new Date().toISOString().split('T')[0]}.xlsx`;
        XLSX.writeFile(wb, filename);
        return true;
    } catch (err) {
        console.error('Export Excel error:', err);
        throw err;
    }
};

/**
 * Prepare and export payroll data to Excel
 */
export const preparePayroll = (filteredEntries, dateFilter) => {
    try {
        const payrollData = {};

        filteredEntries.forEach(entry => {
            if (entry.status !== 'approved') return;

            const empName = entry.expand?.worker?.name || entry.expand?.worker?.username || 'Inconnu';
            const weekNum = getWeek(new Date(entry.date));
            const year = new Date(entry.date).getFullYear();
            const weekKey = `${year}-W${weekNum}`;

            if (!payrollData[empName]) {
                payrollData[empName] = {
                    name: empName,
                    weeks: {},
                    totalBaskets: 0,
                    totalHours: 0
                };
            }

            if (!payrollData[empName].weeks[weekKey]) {
                payrollData[empName].weeks[weekKey] = 0;
            }

            payrollData[empName].weeks[weekKey] += (entry.hours || 0);
            payrollData[empName].totalHours += (entry.hours || 0);

            if ((entry.hours || 0) > 6) {
                payrollData[empName].totalBaskets += 1;
            }
        });

        const finalReport = Object.values(payrollData).map(emp => {
            let regularHours = 0;
            let overtime25 = 0;
            let overtime50 = 0;

            Object.values(emp.weeks).forEach(weeklyHours => {
                if (weeklyHours > 43) {
                    regularHours += 35;
                    overtime25 += 8;
                    overtime50 += (weeklyHours - 43);
                } else if (weeklyHours > 35) {
                    regularHours += 35;
                    overtime25 += (weeklyHours - 35);
                } else {
                    regularHours += weeklyHours;
                }
            });

            return {
                'Employé': emp.name,
                'Heures Totales': emp.totalHours.toFixed(2),
                'H. Normales (HN)': regularHours.toFixed(2),
                'H. Supp 25%': overtime25.toFixed(2),
                'H. Supp 50%': overtime50.toFixed(2),
                'Paniers Repas': emp.totalBaskets,
                'Période': dateFilter === 'ALL' ? 'Tout' : dateFilter
            };
        });

        if (finalReport.length === 0) return { empty: true };

        const ws = XLSX.utils.json_to_sheet(finalReport);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Prépa Paie');

        ws['!cols'] = [
            { wch: 20 }, { wch: 15 }, { wch: 15 },
            { wch: 15 }, { wch: 15 }, { wch: 15 }, { wch: 20 }
        ];

        const filename = `Prepa_Paie_${new Date().toISOString().split('T')[0]}.xlsx`;
        XLSX.writeFile(wb, filename);
        return { success: true, count: finalReport.length };
    } catch (err) {
        console.error('Payroll export error:', err);
        throw err;
    }
};

/**
 * Export filtered entries to PDF
 */
export const exportToPDF = (filteredEntries, activeFilters) => {
    try {
        const doc = new jsPDF('l', 'mm', 'a4');

        doc.setFontSize(18);
        doc.setFont('helvetica', 'bold');
        doc.text('Relevé d\'Heures', 14, 20);

        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        const today = new Date().toLocaleDateString('fr-FR');
        doc.text(`Généré le: ${today}`, 14, 28);

        let filterText = 'Filtres: ';
        const filters = [];
        if (activeFilters.date !== 'ALL') filters.push(`Période: ${activeFilters.date}`);
        if (activeFilters.employee !== 'ALL') filters.push(`Employé: ${activeFilters.employee}`);
        if (activeFilters.chantier !== 'ALL') filters.push(`Chantier: ${activeFilters.chantier}`);
        if (activeFilters.status !== 'ALL') filters.push(`Statut: ${activeFilters.status}`);
        filterText += filters.length > 0 ? filters.join(', ') : 'Aucun';
        doc.text(filterText, 14, 34);

        const tableData = filteredEntries.map(entry => {
            const statusBadge = getStatusBadge(entry.status);
            return [
                new Date(entry.date).toLocaleDateString('fr-FR'),
                entry.expand?.worker?.name || entry.expand?.worker?.username || 'Inconnu',
                entry.expand?.chantier?.name || '-',
                `${entry.hours}h`,
                (entry.description || '-').substring(0, 50) + (entry.description?.length > 50 ? '...' : ''),
                statusBadge.label
            ];
        });

        doc.autoTable({
            head: [['Date', 'Employé', 'Chantier', 'Heures', 'Description', 'Statut']],
            body: tableData,
            startY: 40,
            styles: { fontSize: 9, cellPadding: 3 },
            headStyles: { fillColor: [164, 198, 57], textColor: [255, 255, 255], fontStyle: 'bold' },
            columnStyles: {
                0: { cellWidth: 25 }, 1: { cellWidth: 40 }, 2: { cellWidth: 50 },
                3: { cellWidth: 20 }, 4: { cellWidth: 80 }, 5: { cellWidth: 25 }
            },
            didParseCell: function (data) {
                if (data.column.index === 5 && data.section === 'body') {
                    const status = data.cell.text[0];
                    if (status === 'Validé') data.cell.styles.textColor = [16, 185, 129];
                    else if (status === 'Rejeté') data.cell.styles.textColor = [239, 68, 68];
                    else if (status.includes('Attente') || status === 'À valider') data.cell.styles.textColor = [245, 158, 11];
                    data.cell.styles.fontStyle = 'bold';
                }
            },
            margin: { top: 40, right: 14, bottom: 20, left: 14 }
        });

        const totalHours = filteredEntries.reduce((sum, e) => sum + (e.hours || 0), 0);
        const finalY = doc.lastAutoTable.finalY || 40;
        doc.setFontSize(11);
        doc.setFont('helvetica', 'bold');
        doc.text(`Total des heures: ${totalHours}h`, 14, finalY + 10);
        doc.text(`Nombre d'entrées: ${filteredEntries.length}`, 14, finalY + 16);

        const filename = `Heures_${new Date().toISOString().split('T')[0]}.pdf`;
        doc.save(filename);
        return true;
    } catch (err) {
        console.error('Export PDF error:', err);
        throw err;
    }
};
